import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMemo, useState, ReactNode, useContext, createContext } from 'react';
import { useTranslate } from 'src/locales';

interface ConfirmDialogContextType {
  openConfirmDialog: (
    callback: () => void,
    options?: {
      title?: string;
      message?: string;
      inputLabel?: string;
      requireInput?: boolean;
      validateInput?: (input: string) => boolean;
      errorMessage?: string;
      onSuccess?: () => void;
    }
  ) => void;
  closeDialog: () => void;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextType | undefined>(undefined);

export function ConfirmInputDialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);
  const [onSuccess, setOnSuccess] = useState<(() => void) | null>(null);
  const [requireInput, setRequireInput] = useState(false);
  const [validateInput, setValidateInput] = useState<((input: string) => boolean) | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Input State
  const [inputLabel, setInputLabel] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);

  const { t: tCommon } = useTranslate('common');

  const openConfirmDialog = (
    callback: () => void,
    options?: {
      title?: string;
      message?: string;
      inputLabel?: string;
      requireInput?: boolean;
      validateInput?: (input: string) => boolean;
      errorMessage?: string;
      onSuccess?: () => void;
    }
  ) => {
    setTitle(options?.title || tCommon('confirmAction'));
    setMessage(options?.message || tCommon('areYouSure'));
    setOnConfirm(() => callback);
    setOnSuccess(() => options?.onSuccess || null);
    setRequireInput(!!options?.requireInput);
    setValidateInput(() => options?.validateInput || null);
    setErrorMessage(options?.errorMessage || tCommon('invalidInput'));
    setInputLabel(options?.requireInput ? tCommon('inputLabel') : '');
    setInputValue('');
    setInputError(null);
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setOnConfirm(null);
    setOnSuccess(null);
    setInputValue('');
    setInputError(null);
  };

  const handleConfirm = () => {
    if (requireInput && validateInput) {
      if (!validateInput(inputValue)) {
        setInputError(errorMessage);
        return;
      }
    }
    onConfirm?.();
    onSuccess?.();
  };

  const contextValue = useMemo(() => ({ openConfirmDialog, closeDialog }), []);

  return (
    <ConfirmDialogContext.Provider value={contextValue}>
      {children}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={closeDialog}
        sx={{ borderRadius: 2, '& .MuiPaper-root': { borderRadius: 4 } }}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {message}
          </Typography>
          {requireInput && (
            <TextField
              label={inputLabel}
              variant="outlined"
              fullWidth
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setInputError(null);
              }}
              error={!!inputError}
              helperText={inputError}
              margin="dense"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button variant="outlined" color="error" onClick={closeDialog} sx={{ borderRadius: 2 }}>
            {tCommon('cancel')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirm}
            sx={{ borderRadius: 2 }}
          >
            {tCommon('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmInputDialog() {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirmInputDialog must be used within a ConfirmInputDialogProvider');
  }
  return context;
}
